import { useCallback } from 'react';
import { useHoaDonMuaHangTrongNuoc } from '@/hooks/queries/useHoaDonMuaHangTrongNuoc';
import { extractApiErrorMessage } from '@/lib/error-utils';
import { QUERY_KEYS } from '@/constants/query-keys';
import { toast, useCRUD, usePatch } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseHoaDonMuaHangTrongNuocMutationProps {
  tableState: MuaHangRecord;
}

export const useHoaDonMuaHangTrongNuocMutation = ({ tableState }: UseHoaDonMuaHangTrongNuocMutationProps) => {
  const { entity } = useAuth();
  const { patchItem } = usePatch({
    endpoint: `${QUERY_KEYS.DON_HANG_MUA_TRONG_NUOC}/chi-tiet`
  });
  const { getHoaDonMuaHangTrongNuocDetail } = useHoaDonMuaHangTrongNuoc();
  const { deleteItem: deletePhieuChi } = useCRUD({ endpoint: QUERY_KEYS.PHIEU_CHI, initialFetch: false });
  const { deleteItem: deleteGiayBaoNo } = useCRUD({ endpoint: QUERY_KEYS.GIAY_BAO_NO, initialFetch: false });
  const { deleteItem: deletePhieuNhapKho } = useCRUD({
    endpoint: QUERY_KEYS.PHIEU_NHAP_KHO,
    initialFetch: false
  });

  const handleBulkDelete = useCallback(
    async (uuids: string[]) => {
      await api.post(`/entities/${entity?.slug}/erp/${QUERY_KEYS.HOA_DON_MUA_HANG_TRONG_NUOC}/bulk-delete/`, {
        list: uuids
      });
      await tableState.crud.refreshData();
    },
    [entity?.slug, tableState.crud]
  );

  const handleDelete = useCallback(async () => {
    const selectedObj = tableState.selection.selectedObj as MuaHangRecord;

    if (!selectedObj) return;

    await tableState.crud.deleteItem(selectedObj.uuid);
    selectedObj?.chi_tiet_data?.forEach((item: MuaHangRecord) => {
      patchItem(item.line_dh.uuid, { sl_cl: Number(item.line_dh.sl_cl) + Number(item.so_luong) });
    });

    tableState.form.handleCloseDelete();
    tableState.selection.clearSelection();
    tableState.form.handleCloseForm();
    await tableState.crud.refreshData();
  }, [patchItem, tableState.crud, tableState.form, tableState.selection]);

  const handleEditClick = useCallback(async () => {
    const selectedObj = tableState.selection.selectedObj as MuaHangRecord;

    if (!selectedObj) return;

    try {
      if (
        selectedObj.pc_tao_yn === false &&
        ((Array.isArray(selectedObj.phieu_chi_uuids) && selectedObj.phieu_chi_uuids.length > 0) ||
          (Array.isArray(selectedObj.giay_bao_no_uuids) && selectedObj.giay_bao_no_uuids.length > 0))
      ) {
        toast({
          description: 'Hóa đơn đã thanh toán, không thể chỉnh sửa.',
          variant: 'default'
        });
        return;
      }

      tableState.form.handleEditClick();
    } catch (error) {
      console.error('Error loading detail for edit:', error);
      tableState.form.handleEditClick();
    }
  }, [tableState.form, tableState.selection.selectedObj]);

  const handleSubmit = useCallback(
    async (values: MuaHangRecord) => {
      const { data, detailRows } = values;
      const selectedObj = tableState.selection.selectedObj as MuaHangRecord;
      const formMode = tableState.form.formMode;

      try {
        if (formMode === 'add') {
          await tableState.crud.addItem(data);

          if (Array.isArray(data.chi_tiet_hoa_don)) {
            for (const chiTiet of data.chi_tiet_hoa_don) {
              const chiTietHoaDon = detailRows.find((ct: MuaHangRecord) => ct.uuid === chiTiet.line_dh);
              if (chiTiet.line_dh) {
                await patchItem?.(chiTiet.line_dh, { sl_cl: chiTietHoaDon?.sl_cl - chiTiet.so_luong });
              }
            }
          }
        } else if (formMode === 'edit' && selectedObj) {
          await tableState.crud.updateItem(selectedObj.uuid, data);

          if (Array.isArray(data.chi_tiet_hoa_don)) {
            for (const chiTiet of data.chi_tiet_hoa_don) {
              const chiTietPhieu = selectedObj?.chi_tiet_hoa_don?.find(
                (ct: MuaHangRecord) => ct.line_dh?.uuid === chiTiet.line_dh
              );
              const detailRow = detailRows.find((ct: MuaHangRecord) => ct.line_dh?.uuid === chiTiet.line_dh);

              if (chiTiet.line_dh) {
                await patchItem?.(chiTiet.line_dh, {
                  sl_cl: Number(detailRow?.line_dh?.sl_cl) - Number(chiTiet.so_luong) + Number(chiTietPhieu?.so_luong)
                });
              }
            }
          }
        }

        if (formMode === 'edit' && selectedObj && !data.pc_tao_yn) {
          try {
            const detail = await getHoaDonMuaHangTrongNuocDetail(selectedObj.uuid);

            if ((detail as MuaHangRecord)?.phieu_chi_uuids?.length === 1) {
              await deletePhieuChi((detail as MuaHangRecord).phieu_chi_uuids[0]);
            }

            if ((detail as MuaHangRecord)?.giay_bao_no_uuids?.length === 1) {
              await deleteGiayBaoNo((detail as MuaHangRecord).giay_bao_no_uuids[0]);
            }
          } catch (error) {
            console.error('Error deleting related payment documents:', error);
          }
        }

        if (formMode === 'edit' && selectedObj && !data.pn_yn) {
          try {
            const detail = await getHoaDonMuaHangTrongNuocDetail(selectedObj.uuid);
            if ((detail as MuaHangRecord)?.phieu_nhap_kho_uuid) {
              await deletePhieuNhapKho((detail as MuaHangRecord).phieu_nhap_kho_uuid);
            }
          } catch (error) {
            console.error('Error deleting related warehouse receipt:', error);
          }
        }

        tableState.selection.clearSelection();
        await tableState.crud.refreshData();
        tableState.form.handleCloseForm();
      } catch (error) {
        console.error('Error submitting purchase invoice:', error);
        toast({
          description: extractApiErrorMessage({ data: error }),
          variant: 'destructive'
        });
      }
    },
    [
      deleteGiayBaoNo,
      deletePhieuChi,
      deletePhieuNhapKho,
      getHoaDonMuaHangTrongNuocDetail,
      patchItem,
      tableState.crud,
      tableState.form,
      tableState.selection
    ]
  );

  return {
    handleBulkDelete,
    handleDelete,
    handleEditClick,
    handleSubmit
  };
};
